import { useEffect, useState } from 'react';
import { defaultSelectedValue, OptionsGroup, SelectedValue } from './base-select';

type OnSearchInput = (seachString: string) => void;

const getOptionResults = (
  searchTerm: string,
  optionsGroups: OptionsGroup[],
  canSearchSubText = false
) =>
  optionsGroups.reduce((results: OptionsGroup[], group: OptionsGroup) => {
    const matchedOptions = group.options.filter((option) => {
      let searchText = option.label ?? '';
      if (canSearchSubText) {
        searchText += option.subText;
      }
      return searchText?.toLowerCase().includes(searchTerm.toLowerCase());
    });
    if (!matchedOptions.length && !group.emptyState) {
      return results;
    }
    const { title = '', titleButton = undefined, emptyState = '' } = group;
    return results.concat({
      title,
      titleButton,
      emptyState,
      options: matchedOptions,
    });
  }, []);

interface UseSelectReturn {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedValue: SelectedValue;
  setSelectedValue: React.Dispatch<React.SetStateAction<SelectedValue>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  optionsResults: OptionsGroup[];
  setOptionsResults: React.Dispatch<React.SetStateAction<OptionsGroup[]>>;
}

export const useSelect: (
  show: boolean,
  optionsGroups: OptionsGroup[],
  onSearchInput?: OnSearchInput,
  defaultSelected?: SelectedValue,
  canSearchSubText?: boolean
) => UseSelectReturn = (
  show: boolean,
  optionsGroups: OptionsGroup[],
  onSearchInput?: OnSearchInput,
  defaultSelected = defaultSelectedValue,
  canSearchSubText?: boolean
) => {
  const [visible, setVisible] = useState<boolean>(show);
  const [selectedValue, setSelectedValue] = useState<SelectedValue>(defaultSelected);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [optionsResults, setOptionsResults] = useState<OptionsGroup[]>(optionsGroups);

  useEffect(() => {
    /**
     * If onSearchInput is provided don't filter value
     */
    if (onSearchInput) {
      onSearchInput(searchTerm);
      setOptionsResults(optionsGroups);
      return;
    }

    /** Filter Options Logic */
    setOptionsResults(getOptionResults(searchTerm, optionsGroups, canSearchSubText));
  }, [onSearchInput, optionsGroups, searchTerm, canSearchSubText]);

  return {
    visible,
    setVisible,
    selectedValue,
    setSelectedValue,
    searchTerm,
    setSearchTerm,
    optionsResults,
    setOptionsResults,
  };
};
